import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Platform,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useToast } from '@/contexts/admin/ToastContext';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { ReportReason } from '@/types/residents/marketplaceListing';
import { 
  BreadcrumbNavigation, 
  MarketplaceScreenLayout,
  SellerChatModal, 
  PhotoGalleryModal,
  ImageCarousel,
  ReportModal,
  InlineConversation,
} from '@/components/residents/marketplace';
import {
  SellerCard,
  ItemDetailsSection,
  ActionButtonRow,
} from '@/components/residents/marketplace/shared';
import { 
  MARKETPLACE_MAX_CONTENT_WIDTH,
  getResponsivePadding,
  calculateCarouselDimensions,
} from '@/utils/marketplace';
import { useToggleSavedItemMutation, useCreateReportMutation, useLazyGetListingQuery, useGetConversationByListingQuery, profileApi } from '@/store/api';
import { useAppDispatch } from '@/store';
import { CACHE_TAGS } from '@/constants/residents/marketplaceConstants';

type MarketplaceItemDetailNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceItemDetail'>;
type MarketplaceItemDetailRouteProp = RouteProp<RootStackParamList, 'MarketplaceItemDetail'>;

export const MarketplaceItemDetailScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceItemDetailNavigationProp>();
  const route = useRoute<MarketplaceItemDetailRouteProp>();
  const dispatch = useAppDispatch();
  const { layoutMode, width: screenWidth, isWeb } = useResponsive();
  const { showSuccess } = useToast();
  
  // Responsive layout detection
  const isMobile = layoutMode === 'mobile';
  const isTablet = layoutMode === 'tablet';
  const isDesktop = layoutMode === 'desktop';
  
  // Responsive values
  const contentPadding = getResponsivePadding(layoutMode);
  const maxContentWidth = isTablet 
    ? MARKETPLACE_MAX_CONTENT_WIDTH.tablet 
    : isDesktop 
      ? MARKETPLACE_MAX_CONTENT_WIDTH.desktop 
      : undefined;
  
  // Calculate carousel dimensions based on layout
  const carouselDimensions = calculateCarouselDimensions(layoutMode, screenWidth);
  
  const { listing: initialListing } = route.params;
  const [listing, setListing] = useState(initialListing);
  const [isBookmarked, setIsBookmarked] = useState(initialListing.isBookmarked);
  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [hasAlreadyReported, setHasAlreadyReported] = useState(false);

  // API mutations and queries
  const [toggleSavedItem] = useToggleSavedItemMutation();
  const [createReport] = useCreateReportMutation();
  const [getListing] = useLazyGetListingQuery();
  
  // Fetch existing conversation for this listing (buyer side)
  const { 
    data: existingConversation,
    refetch: refetchConversation,
  } = useGetConversationByListingQuery(initialListing.id);
  
  // Determine if buyer has an existing conversation with the seller
  const hasExistingConversation = !!existingConversation;

  // Track view on mount, update with fresh data, and invalidate recently viewed cache
  useEffect(() => {
    const trackViewAndRefresh = async () => {
      try {
        const freshListing = await getListing(initialListing.id).unwrap();
        if (freshListing) {
          setListing(freshListing);
          setIsBookmarked(freshListing.isBookmarked);
        }
        // Invalidate RecentlyViewed cache so the list updates when user goes back
        dispatch(profileApi.util.invalidateTags([{ type: CACHE_TAGS.RecentlyViewed, id: 'LIST' }]));
      } catch (error) {
        // Silently fail - we still have the initial listing data
        console.warn('Failed to fetch fresh listing data:', error);
      }
    };
    trackViewAndRefresh();
  }, [initialListing.id, getListing, dispatch]);

  const handleGoBack = () => {
    navigation.goBack();
  };

  const handleGoToMarketplace = () => {
    if (isWeb && isDesktop) {
      navigation.navigate('Marketplace' as any);
    } else {
      navigation.navigate('Main', { screen: 'ResidentMarketplace' } as any);
    }
  };

  const handleBookmarkPress = async () => {
    const newBookmarkState = !isBookmarked;
    setIsBookmarked(newBookmarkState);
    
    try {
      await toggleSavedItem(listing.id).unwrap();
      if (newBookmarkState) {
        showSuccess('Added to your saved items!');
      }
    } catch (error) {
      // Revert on error
      setIsBookmarked(!newBookmarkState);
      console.error('Failed to toggle saved item:', error);
    }
  };

  const handleContactSeller = () => {
    setIsChatModalVisible(true);
  };

  const handleCloseChatModal = () => {
    setIsChatModalVisible(false);
    // Refetch conversation in case user started a new one
    refetchConversation();
  };

  const handleReportListing = () => {
    setIsReportModalVisible(true);
  };

  const handleCloseReportModal = () => {
    setIsReportModalVisible(false);
  };

  const handleSubmitReport = async (reason: ReportReason, description?: string) => {
    try {
      await createReport({
        listingId: listing.id,
        reason,
        description,
      }).unwrap();
      
      // Close modal and show success toast
      setIsReportModalVisible(false);
      showSuccess('Report submitted. It will be reviewed by the moderation team.');
      
      // Mark as already reported for this session
      setHasAlreadyReported(true);
    } catch (error: any) {
      // Check if already reported (409 Conflict)
      if (error?.status === 409) {
        setHasAlreadyReported(true);
      }
      console.error('Failed to submit report:', error);
    }
  };

  const handleImagePress = (index: number) => {
    setGalleryInitialIndex(index);
    setIsGalleryVisible(true);
  };

  const handleCloseGallery = () => {
    setIsGalleryVisible(false);
  };

  const handleGalleryContactSeller = () => {
    setIsGalleryVisible(false);
    // Small delay to let the gallery close animation finish
    setTimeout(() => {
      setIsChatModalVisible(true);
    }, 100);
  };

  const handleSellerPress = () => {
    navigation.navigate('SellerProfile', { sellerId: listing.seller.id });
  };

  return (
    <>
      <MarketplaceScreenLayout navigatorTitle="Item details">
        <KeyboardAwareScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.content,
            { padding: contentPadding },
            !isMobile && styles.contentLarge,
          ]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          enableOnAndroid={true}
          enableAutomaticScroll={true}
          extraHeight={Platform.OS === 'ios' ? 140 : 100}
          keyboardOpeningTime={0}
          enableResetScrollToCoords={false}
        >
          {/* Responsive content wrapper */}
          <View style={[
            styles.contentWrapper,
            maxContentWidth ? { maxWidth: maxContentWidth, alignSelf: 'center', width: '100%' } : undefined,
          ]}>
            {/* Back Navigation */}
            <BreadcrumbNavigation 
              onHomePress={handleGoToMarketplace}
              onBackPress={handleGoBack}
            />

            {/* Image Carousel - centered on desktop */}
            <View style={!isMobile && styles.carouselContainer}>
              <ImageCarousel
                images={listing.images.map(img => img.url)}
                onImagePress={handleImagePress}
                showBookmark={true}
                isBookmarked={isBookmarked}
                onBookmarkPress={handleBookmarkPress}
                width={!isMobile ? carouselDimensions.width : undefined}
                height={!isMobile ? carouselDimensions.height : undefined}
                layoutMode={layoutMode}
                showArrows={!isMobile}
              />
            </View>

            {/* Details Section */}
            <ItemDetailsSection
              price={listing.price}
              title={listing.title}
              description={listing.description}
              condition={listing.condition}
              pickupOption={listing.pickupOption}
            />

            {/* Seller Card */}
            <SellerCard
              seller={listing.seller}
              onPress={handleSellerPress}
            />

            {/* Action Buttons - show only if no existing conversation */}
            {!hasExistingConversation && (
              <ActionButtonRow
                onContactPress={handleContactSeller}
                onReportPress={handleReportListing}
              />
            )}

            {/* Inline Conversation - show if buyer has messaged the seller */}
            {hasExistingConversation && (
              <InlineConversation
                listingId={listing.id}
              />
            )}

            {/* Report button when conversation exists */}
            {hasExistingConversation && (
              <ActionButtonRow
                onReportPress={handleReportListing}
                hideContactButton={true}
              />
            )}
          </View>
        </KeyboardAwareScrollView>
      </MarketplaceScreenLayout>

      {/* Modals - rendered outside the layout */}
      <SellerChatModal
        visible={isChatModalVisible}
        onClose={handleCloseChatModal}
        listing={listing}
      />
      <PhotoGalleryModal
        visible={isGalleryVisible}
        onClose={handleCloseGallery}
        images={listing.images.map(img => img.url)}
        initialIndex={galleryInitialIndex}
        onContactSeller={handleGalleryContactSeller}
        onReport={handleReportListing}
      />
      <ReportModal
        visible={isReportModalVisible}
        onClose={handleCloseReportModal}
        onSubmit={handleSubmitReport}
        hasAlreadyReported={hasAlreadyReported}
      />
    </>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    gap: 20,
  },
  contentLarge: {
    gap: 28,
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 20,
  },
  carouselContainer: {
    alignItems: 'center',
  },
});
